import { logger } from "@/core/logging";

import { IStorageAdapter } from "../IStorageAdapter";

export default class LocalStorageAdapter implements IStorageAdapter {
  getItem<T>(key: string): Promise<T | null> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve(null);
    }

    const stored = storage.getItem(key);
    if (stored === null) {
      return Promise.resolve(null);
    }

    try {
      return Promise.resolve(JSON.parse(stored) as T);
    } catch {
      logger.error("local_storage_get_item_failed", { key });
      return Promise.resolve(null);
    }
  }

  getItemSync<T>(key: string): T | null {
    const storage = this.getStorage();
    if (!storage) {
      return null;
    }

    const stored = storage.getItem(key);
    if (stored === null) {
      return null;
    }

    try {
      return JSON.parse(stored) as T;
    } catch {
      logger.error("local_storage_get_item_sync_failed", { key });
      return null;
    }
  }

  removeItemSync(key: string): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    storage.removeItem(key);
  }

  removeItemsByPrefixSync(prefixes: readonly string[]): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const keysToRemove = this.getStorageKeys(storage).filter((key) =>
      prefixes.some((prefix) => key.startsWith(prefix)),
    );

    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  }

  setItem<T>(key: string, value: T): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve();
    }

    const serialized = this.stringifySafely(value);
    if (serialized === null) {
      return Promise.resolve();
    }

    try {
      return Promise.resolve(storage.setItem(key, serialized));
    } catch {
      return Promise.resolve();
    }
  }

  setItemSync<T>(key: string, value: T): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const serialized = this.stringifySafely(value);
    if (serialized === null) {
      return;
    }

    try {
      storage.setItem(key, serialized);
    } catch {
      return;
    }
  }

  removeItem(key: string): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve();
    }

    return Promise.resolve(storage.removeItem(key));
  }

  clear(): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve();
    }

    return Promise.resolve(storage.clear());
  }

  private stringifySafely(value: unknown): string | null {
    try {
      return JSON.stringify(value);
    } catch {
      return null;
    }
  }

  private getStorage(): Storage | null {
    return globalThis.localStorage;
  }

  private getStorageKeys(storage: Storage): string[] {
    const keys: string[] = [];

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key) {
        keys.push(key);
      }
    }

    return keys;
  }
}
